import { useEffect, useState } from "react";
import api, { formatDate } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { PageHeader, useConfirm } from "../../components/common";
import { Plus, ShieldCheck, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ROLE_LABELS } from "../../lib/areas";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export default function AdminUsers() {
  const { user } = useAuth();
  const [ask, confirmEl] = useConfirm();
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const [pw, setPw] = useState(null);
  const load = () => api.get("/admin-users").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const err = (e) => { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : "Bilgileri kontrol edin (şifre en az 6 karakter)"); };
  const create = async () => { try { await api.post("/admin-users", form); toast.success("Yönetici eklendi"); setForm(null); load(); } catch (e) { err(e); } };
  const toggle = async (u) => { try { await api.put(`/admin-users/${u.id}`, { is_active: !u.is_active }); load(); } catch (e) { err(e); } };
  const setRole = async (u, role) => { try { await api.put(`/governance/admins/${u.id}/role`, { role }); toast.success("Rol güncellendi"); load(); } catch (e) { err(e); } };
  const changePw = async () => { try { await api.put(`/admin-users/${pw.id}`, { password: pw.password }); toast.success("Şifre değişti"); setPw(null); } catch (e) { err(e); } };

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <PageHeader eyebrow="Sistem" title="Kullanıcılar" subtitle="Yönetim paneline erişebilen kullanıcılar. Teknisyenler “Teknisyenler” sayfasından yönetilir.">
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setForm({ name: "", email: "", password: "", phone: "", admin_role: "manager" })} data-testid="new-admin"><Plus className="h-4 w-4 mr-2" />Yönetici Ekle</Button>
      </PageHeader>
      <Card className="border border-slate-200 shadow-none overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Ad</th><th className="px-4 py-3">E-posta</th><th className="px-4 py-3">Rol</th><th className="px-4 py-3">2FA</th><th className="px-4 py-3">Eklendi</th><th className="px-4 py-3">Aktif</th><th className="px-4 py-3" /></tr>
          </thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.id} className="border-b border-slate-100">
                <td className="px-4 py-3 font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-blue-600" />{u.name}{u.id === user?.id && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">siz</span>}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3">
                  <Select value={u.admin_role || "owner"} onValueChange={(v) => setRole(u, v)}>
                    <SelectTrigger className="h-8 w-36" data-testid={`role-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 text-xs">{u.totp_enabled ? <span className="text-emerald-700 font-semibold">Açık <button className="ml-1 underline text-slate-500" onClick={async () => { if (await ask("2FA sıfırlansın mı?", `${u.name} kendi kurulumunu yeniden yapmalı.`)) { await api.post(`/governance/admins/${u.id}/reset-2fa`); load(); } }}>sıfırla</button></span> : <span className="text-slate-400">Kapalı</span>}</td>
                <td className="px-4 py-3 text-slate-500">{formatDate(u.created_at)}</td>
                <td className="px-4 py-3"><Switch checked={u.is_active !== false} disabled={u.id === user?.id} onCheckedChange={() => toggle(u)} /></td>
                <td className="px-4 py-3 text-right"><Button size="sm" variant="ghost" onClick={() => setPw({ id: u.id, name: u.name, password: "" })}><KeyRound className="h-4 w-4 mr-1.5" />Şifre</Button>{u.id !== user?.id && <Button size="sm" variant="ghost" className="text-rose-600" data-testid={`delete-admin-${u.id}`} onClick={async () => { if (await ask(`${u.name} silinsin mi?`, "Bu yönetici bir daha giriş yapamaz.")) { try { await api.delete(`/admin-users/${u.id}`); toast.success("Silindi"); load(); } catch (e) { err(e); } } }}><Trash2 className="h-4 w-4" /></Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent>
          {form && (<>
            <DialogHeader><DialogTitle>Yönetici Ekle</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Ad Soyad *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="au-name" /></div>
              <div><Label>E-posta *</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="au-email" /></div>
              <div><Label>Şifre * (en az 6)</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="au-password" /></div>
              <div><Label>Rol</Label>
                <Select value={form.admin_role} onValueChange={(v) => setForm({ ...form, admin_role: v })}>
                  <SelectTrigger data-testid="au-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">Müdür: finans/site dahil, güvenlik ve yedek hariç · Muhasebe: yalnızca finans · Operasyon: iş/usta · Site editörü: yalnızca Stüdyo</p>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setForm(null)}>Vazgeç</Button><Button className="bg-blue-600 hover:bg-blue-700" onClick={create} data-testid="au-save">Ekle</Button></DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>
      <Dialog open={!!pw} onOpenChange={(o) => !o && setPw(null)}>
        <DialogContent>
          {pw && (<>
            <DialogHeader><DialogTitle>{pw.name} — Yeni Şifre</DialogTitle></DialogHeader>
            <Input type="password" placeholder="En az 6 karakter" value={pw.password} onChange={(e) => setPw({ ...pw, password: e.target.value })} />
            <DialogFooter><Button variant="outline" onClick={() => setPw(null)}>Vazgeç</Button><Button className="bg-blue-600 hover:bg-blue-700" onClick={changePw}>Değiştir</Button></DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>
      {confirmEl}
    </div>
  );
}
